/**
 * Keys purged from `localStorage` whenever the auth state is reset. The only
 * caller is `resetUserScopedSession()` (stores/Auth.ts), which runs this and then
 * dispatches the `tmi:auth-reset` event so user-scoped stores drop their
 * in-memory copies. `Auth.reset()` runs it once the token is gone, and a token
 * handoff runs it after assigning the next member's token (boot-auth-2).
 * The router's auth guard does NOT purge (tmi-6gh, LD1; pinned by auth-guard.spec.ts).
 *
 * This list used to be seven inline `localStorage.removeItem` calls with
 * zero test coverage (R7). Pulling it out gives the purge behavior a spec
 * home and makes adding a new user-scoped key (e.g. `sss-filters`) a single
 * line instead of a silent gap.
 *
 * UX roadmap #63: state a member should find again after signing back in on the same device (read state, bell
 * acknowledgements) is kept per member under an owner-scoped key instead (utils/storage-owner.ts), so it is not here.
 * Device preferences (theme, sidebar panels, the notifications toggle, table columns, calculator inputs) are not either.
 */
export const USER_SCOPED_PURGE_KEYS = [
    // UX roadmap #78: which members hid the Dashboard's SMS sign-up card, by user id (SmsEnrollment.vue)
    "dashboard_sms_dismissed",
    // UX roadmap #63: when a member last signed up for SMS alerts from the Dashboard, by user id (SmsEnrollment.vue)
    "dashboard_sms_pending_enrollment",
    "fav_art",
    "fav_sec",
    "fav_web",
    "model-portfolios",
    // UX roadmap #9: when 'model-portfolios' was last fetched; stale-while-revalidate reads it as "fresh" (ModelPortfolios.ts)
    "model-portfolios-updated-at",
    "my-portfolios",
    // The trade alerts a member's tabs have already toasted, so the other tabs do not repeat them (composables/new-trade-alerts.ts)
    "new-trade-alerts-announced",
    // UX roadmap #63: the member's SMS enrollments, with their phone number (stores/User.ts)
    "notification_enrollments",
    "permissions",
    // nav-10: the tickers a member last opened from search (composables/useRecentTickers.ts)
    "recent-tickers",
    // UX roadmap #63: the member's billing profile link (stores/User.ts)
    "recurly_profile_link",
    // UX roadmap #63: the portfolio the member last picked in the Portfolio Builder (stores/MyPortfolios.ts)
    "selected-portfolio",
    "user",
    // UX roadmap #95: one member's resume positions and watched videos (composables/useVideoProgress.ts)
    "video-progress",
    "sss-filters",
] as const

export function purgeUserScopedStorage(): void {
    for (const key of USER_SCOPED_PURGE_KEYS) {
        localStorage.removeItem(key)
    }
}
